import { useState, useEffect, useRef } from "react";
import ProfileModal from "./ProfileModal";

export default function ProfileDropdown({ user, onSignOut }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [smallImageError, setSmallImageError] = useState(false);
  const [largeImageError, setLargeImageError] = useState(false);
  const dropdownRef = useRef(null);

  // Reset image errors when user picture changes
  useEffect(() => {
    setSmallImageError(false);
    setLargeImageError(false);
  }, [user?.picture]);

        // Convert S3 URI to HTTPS URL or return presigned URL as-is
  const getImageUrl = (s3Uri) => {
    if (!s3Uri) return null;
    
    // Trim whitespace from the URI
    s3Uri = s3Uri.trim();
    
    // If it's already a presigned URL (has AWS signature), return it as-is
    if (s3Uri.includes('X-Amz-Algorithm') || s3Uri.includes('X-Amz-Credential')) {
      return s3Uri;
    }
    
    // If it's already an HTTPS URL, return it
    if (s3Uri.startsWith('http://') || s3Uri.startsWith('https://')) {
      return s3Uri;
    }
    
    // Convert s3:// URI to HTTPS URL (legacy support)
    if (s3Uri.startsWith('s3://')) {
      const pathParts = s3Uri.replace('s3://', '').split('/');
      const bucket = pathParts[0];
      const key = pathParts.slice(1).join('/');
      
      // Use the most compatible URL format (without region for us-east-1)
      return `https://${bucket}.s3.amazonaws.com/${key}`;
    }
    
    return null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get display name
  const displayName = user?.name || user?.email?.split("@")[0] || "User";
  const displayEmail = user?.email || "No email available";

  return (
    <>
      <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Profile Icon Button */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        title="Profile"
        style={{
          width: 38,
          height: 38,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "background 0.2s",
          background: isOpen ? "rgba(255,255,255,0.15)" : "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isOpen) e.currentTarget.style.background = "rgba(255,255,255,0.1)";
        }}
        onMouseLeave={(e) => {
          if (!isOpen) e.currentTarget.style.background = "transparent";
        }}
      >
        {/* User Avatar with Initials or Picture */}
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: getImageUrl(user?.picture)
              ? "transparent"
              : "linear-gradient(135deg, #0a1a30 0%, #1a2d4a 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 700,
            color: "#ffffff",
            border: "2px solid rgba(255,255,255,0.3)",
            overflow: "hidden",
          }}
        >
                    {getImageUrl(user?.picture) && !smallImageError ? (
            <img 
              src={getImageUrl(user?.picture)} 
              alt="Profile" 
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
                            onError={() => setSmallImageError(true)}
            />
          ) : (
            getInitials(displayName)
          )}
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 280,
            background: "linear-gradient(180deg, #0e2240 0%, #0a1a30 100%)",
            border: "2px solid rgba(255,255,255,0.25)",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            overflow: "hidden",
            zIndex: 2000,
            animation: "slideDown 0.2s ease-out",
          }}
        >
          {/* User Info Section */}
          <div
            style={{
              padding: "20px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            {/* Large Avatar with Picture */}
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: getImageUrl(user?.picture)
                  ? "transparent"
                  : "linear-gradient(135deg, #0a1a30 0%, #1a2d4a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                fontWeight: 700,
                color: "#ffffff",
                border: "2px solid rgba(255,255,255,0.3)",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
                            {getImageUrl(user?.picture) && !largeImageError ? (
                <img 
                  src={getImageUrl(user?.picture)} 
                  alt="Profile" 
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                                    onError={() => setLargeImageError(true)}
                />
              ) : (
                getInitials(displayName)
              )}
            </div>

            {/* User Details */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#ffffff",
                  marginBottom: 4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.6)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayEmail}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div style={{ padding: "8px" }}>
            {/* Profile Settings */}
            <button
              onClick={() => {
                setIsOpen(false);
                setShowProfileModal(true);
              }}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                borderRadius: 8,
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                cursor: "pointer",
                transition: "background 0.15s",
                color: "#ffffff",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span style={{ fontSize: 14, fontWeight: 500 }}>My Profile</span>
            </button>

            {/* Divider */}
            <div
              style={{
                height: 1,
                background: "rgba(255,255,255,0.1)",
                margin: "8px 0",
              }}
            />

            {/* Logout Button */}
            <button
              onClick={() => {
                setIsOpen(false);
                onSignOut();
              }}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                borderRadius: 8,
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                cursor: "pointer",
                transition: "background 0.15s",
                color: "#ff6b6b",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,107,107,0.1)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Log Out</span>
            </button>
          </div>
        </div>
      )}

    </div>

    {/* Profile Modal */}
    {showProfileModal && (
      <ProfileModal user={user} onClose={() => setShowProfileModal(false)} />
    )}
  </>
  );
}
